/**
 * MobileTabBar — barre de navigation basse du MobileShell.
 *
 * @remarks
 * Remplace le menu hamburger : destinations principales en icône + label.
 * L'onglet actif passe simplement en couleur `text-primary` (icône + label) ;
 * inactif en `text-fg-muted`. Piloté par `aria-current`, posé ici à partir du
 * **contexte courant** et non du préfixe d'URL (cf. `NavigationLink.activeContexts`
 * — le match de `NavLink` allumait le mauvais onglet sur les routes qu'une
 * feature pose sous le préfixe d'une autre).
 *
 * Seuls les liens `top` du registry sont ici : à plus de quatre ou cinq
 * destinations, les onglets ne tiennent plus dans 360–430px (libellés collés,
 * dernier onglet rogné — SAAS-1200). Les liens `bottom` (boutique, achats,
 * parrainage, admin) sont listés à plat dans le menu compte
 * (`MobileAccountMenu`), `Paramètres` aussi y vit.
 *
 * Barre pleine largeur collée en bas (pas de pilule flottante), même fond que
 * l'en-tête pour la détacher du contenu. Le padding bas absorbe la zone de
 * geste iOS quand la page passe en `viewport-fit=cover`.
 */

import { Link } from 'react-router-dom';
import { featureRegistry, type AppContext, type Context } from '@/app/registry';
import { getMobileNavigationLinks } from './navigation-links';

interface Props {
  context: Context;
  appCtx: AppContext;
}

export function MobileTabBar({ context, appCtx }: Props) {
  const { topLinks: links } = getMobileNavigationLinks(
    featureRegistry.getAllNavigationLinks(appCtx),
  );

  if (links.length === 0) return null;

  return (
    <nav
      aria-label="Navigation principale"
      className="border-border bg-bg-muted flex shrink-0 items-stretch justify-around border-t px-1 pb-[env(safe-area-inset-bottom)]"
    >
      {links.map((link) => {
        const Icon = link.icon;
        return (
          <Link
            key={link.id}
            to={link.to}
            aria-label={link.label}
            title={link.label}
            aria-current={link.activeContexts.includes(context) ? 'page' : undefined}
            className="group text-fg-muted aria-[current=page]:text-primary flex min-w-0 flex-1 flex-col items-center gap-0.5 px-1 py-2 transition-colors"
          >
            <Icon className="size-5" strokeWidth={2} aria-hidden />
            <span className="line-clamp-2 text-center text-xs leading-tight font-medium group-aria-[current=page]:font-semibold">
              {link.label}
            </span>
          </Link>
        );
      })}
    </nav>
  );
}
